import { Phone, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EMAIL_ADDRESS, EMAIL_HREF, PHONE_DISPLAY, PHONE_HREF } from "@/lib/landingLinks";

const SLUG = "outsourced-financial-management";

/**
 * The top of the outsourced finance function page.
 *
 * This service isn't priced online, so the hero's job is different from the
 * other landing pages: it has to make the shape of the offer obvious - a led
 * team, not a freelancer - before asking for a scoping conversation.
 */
export const FinanceHero = ({ onScope }) => (
  <section data-testid={`${SLUG}-hero`} className="relative overflow-hidden">
    <div className="tt-hero-wash tt-hero-fade absolute inset-0" aria-hidden="true" />
    <div className="tt-ruled tt-hero-fade absolute inset-0 opacity-50" aria-hidden="true" />

    <div className="relative mx-auto max-w-6xl px-4 pb-12 pt-10 sm:px-6 sm:pb-16 sm:pt-16 lg:px-8">
      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <span className="tt-stamp">Outsourced financial management</span>

          <h1
            data-testid={`${SLUG}-h1`}
            className="mt-6 text-[34px] font-extrabold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-[46px] lg:text-[54px]"
          >
            Your outsourced{" "}
            <span className="relative whitespace-nowrap text-primary">
              finance function
              <span
                aria-hidden="true"
                className="absolute -bottom-1 left-0 h-[3px] w-full rounded-full bg-[color:var(--tt-teal-line)]"
              />
            </span>
          </h1>

          <p className="mt-6 max-w-[56ch] text-base leading-relaxed text-muted-foreground sm:text-[17px]">
            A finance team led by an experienced Financial Manager, running your day-to-day
            finance operations, reporting, cash flow, compliance and financial decision
            support — structured around the level of support your business actually needs.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              size="lg"
              onClick={onScope}
              data-testid={`${SLUG}-hero-scope`}
              className="h-12 min-h-[44px] px-5 text-[15px] font-semibold"
            >
              Scope my finance function
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid={`${SLUG}-hero-call`}
              className="h-12 min-h-[44px] bg-white px-5 text-[15px] font-semibold"
            >
              <a href={PHONE_HREF}>
                <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                {PHONE_DISPLAY}
              </a>
            </Button>
          </div>

          <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
            Flexible, part-time or dedicated finance teams for growing businesses that need
            more than bookkeeping. Or{" "}
            <a
              href={EMAIL_HREF}
              data-testid={`${SLUG}-hero-email`}
              className="tt-link inline-flex items-center gap-1"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              {EMAIL_ADDRESS}
            </a>
          </p>
        </div>

        <div className="lg:col-span-5">
          <div data-testid={`${SLUG}-hero-card`} className="tt-card p-6 sm:p-7">
            <h2 className="tt-eyebrow">What you're taking on</h2>
            <dl className="mt-5 space-y-4">
              {[
                {
                  term: "Financial leadership",
                  detail:
                    "A Financial Manager leads the finance function and coordinates the team around your reporting, controls and financial management requirements.",
                },
                {
                  term: "A reporting rhythm",
                  detail: "Management accounts, cash flow and the few measures your business runs on.",
                },
                {
                  term: "One monthly fee",
                  detail: "One agreed scope and one invoice, however many people the work takes.",
                },
              ].map(({ term, detail }) => (
                <div key={term} className="border-l-[3px] border-l-[color:var(--tt-accent)] pl-4">
                  <dt className="text-[13px] font-bold uppercase tracking-[0.08em] text-foreground">
                    {term}
                  </dt>
                  <dd className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                    {detail}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-6 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
              Scoped, not calculated. We agree the team and the fee with you before anything
              starts.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
